import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { PORawMaterialItem, PurchaseOrder, PurchaseOrderRequest, Supplier } from '../../purchased-order.model';
import { Action, PaginatedResponse, Status, TableColumn } from '../../../common/models/common.model';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { GenericBase } from '../../../common/helpers/generic';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {
  blobErrorMessage,
  downloadBlobResponse,
  formatAmount,
  gstPercentageValidator,
  isFileBlobResponse,
  roundHalfUp,
  SnackbarAlert,
  SnackbarAlertStatus,
  toNumberOrNull,
} from '../../../utils';
import { SnackbarService } from '../../../services/snackbar.service';
import { RawMaterialService } from '../../../raw-material-inventory/raw-material.service';
import { SupplierService } from '../../services/supplier.service';
import { finalize, forkJoin, Subscription } from 'rxjs';
import {
  MeasurementUnit,
  QualityCheckStatus,
  RawMaterialItem,
  RawMaterialWarehouse,
} from '../../../raw-material-inventory/raw-material.model';
import { PurchasedOrderService } from '../../services/purchased-order.service';
import { User } from '../../../authentication/auth.models';
import { SettingsService } from '../../../settings/settings.service';
import { UserRole } from '../../../employees/employees.model';
import { Company } from '../../../production-inventory/production-inventory.model';
import { CompanyFactoryService } from '../../../production-inventory/services/company-factory.service';

@Component({
  selector: 'app-purchased-order',
  standalone: false,
  templateUrl: './purchased-order.component.html',
  styleUrl: './purchased-order.component.scss',
})
export class PurchasedOrderComponent implements OnInit, OnDestroy {
  @ViewChild('createPurchaseOrderContent') createPurchaseOrderContent!: TemplateRef<any>;
  @ViewChild('createRawMaterialEntryContent') createRawMaterialEntryContent!: TemplateRef<any>;
  @ViewChild('purchaseOrderDetailsContent') purchaseOrderDetailsContent!: TemplateRef<any>;

  @Input() wareHouses: RawMaterialWarehouse[] = [];

  protected isPurchasedOrdersDataLoading: boolean = false;
  protected purchasedOrders: PurchaseOrder[] = [];
  protected hasNextPage: boolean = false;
  protected hasPreviousPage: boolean = false;
  protected totalCount: number = 0;
  protected currentPage: number = 1;
  protected modalRef!: NgbModalRef;
  protected mode: Action = Action.CREATE;
  protected purchaseOrderForm!: FormGroup;
  protected suppliers: Supplier[] = [];
  protected companies: Company[] = [];
  protected rawMaterials: RawMaterialItem[] = [];
  protected measurementUnits: MeasurementUnit[] = [];
  protected rawMaterialEntryForm!: FormGroup;
  protected selectedPurchasedOrder: PurchaseOrder | null = null;
  protected viewedPurchaseOrder: PurchaseOrder | null = null;
  protected selectedWarehouse: string | null = null;
  protected selectedCompany: string | null = null;
  protected selectedSupplier: string | null = null;

  /**
   * Every supplier, for the filter. Separate from `suppliers`, which is one page of
   * full records the create form reads addresses and GST numbers from.
   */
  protected supplierOptions: { id: string; name: string }[] = [];
  protected isEditMode: boolean = false;
  protected currentUser: User | null = null;
  protected isPurchasedOrderDataSaving: boolean = false;
  protected isExporting: boolean = false;
  protected isUpdatingPurchasedOrderData: boolean = false;
  protected rawMaterialEntryError: any = null;
  protected purchaseOrderError: any = null;
  protected entryPurchaseOrder: PurchaseOrder | null = null;

  private purchasedOrderSubscription!: Subscription;

  /**
   * Which rate fields the user has typed into, per line. Only the field the user
   * has NOT touched gets auto-derived; if both were typed, both are left alone.
   * Keyed by the line's FormGroup so removing/reordering lines can't misalign it.
   */
  private readonly rateEditsByItem = new WeakMap<AbstractControl, { excl: boolean; incl: boolean }>();

  protected readonly qcStatusOptions = [
    { label: 'Pass', value: 'pass' },
    { label: 'Fail', value: 'fail' },
  ];

  protected readonly paymentStatusOptions = [
    { label: 'Pending', value: 'PENDING' },
    { label: 'Paid', value: 'PAID' },
  ];

  protected readonly approvalStatusOptions = [
    { label: 'Pending', value: false },
    { label: 'Approved', value: true },
  ];

  protected readonly purchasedOrderTableColumns: TableColumn<PurchaseOrder>[] = [
    { label: 'Date', field: 'date_of_order' },
    { label: 'Company', field: 'company_name' },
    { label: 'PO Number', field: 'po_number' },
    { label: 'Supplier Name', field: 'supplier_name' },
    {
      label: 'Place of Delivery',
      field: 'warehouse_name',
    },
    {
      label: 'Total Cost',
      field: 'total_cost',
      format: (value: any) => formatAmount(value),
    },
    {
      label: 'Entry',
      btn_type: 'Add Entry',
    },
  ];

  constructor(
    private modalService: NgbModal,
    private purchasedOrderService: PurchasedOrderService,
    private rawMaterialService: RawMaterialService,
    private supplierService: SupplierService,
    private settingsService: SettingsService,
    private companyFactoryService: CompanyFactoryService,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.settingsService.currentUser$.subscribe(user => {
      this.currentUser = user;

      if (this.currentUser?.role !== UserRole.ADMIN) {
        const currentPlanHeadWareHouse = this.wareHouses.find(wareHouse => {
          return wareHouse.plant_head?.user_id === this.currentUser?.id;
        });

        if (currentPlanHeadWareHouse) {
          this.selectedWarehouse = currentPlanHeadWareHouse.id;
        }
      }
    });

    this.getInitialData();
    this.initializeRawMaterialEntryForm();
  }

  get items(): FormArray {
    return this.purchaseOrderForm.get('items') as FormArray;
  }

  get poItems(): FormArray {
    return this.rawMaterialEntryForm.get('items') as FormArray;
  }

  protected handlePurchasedOrderDelete(purchasedOrder: PurchaseOrder): void {
    this.purchasedOrderSubscription = this.purchasedOrderService.delete(purchasedOrder.id).subscribe({
      next: () => {
        this.purchasedOrders = this.purchasedOrders.filter(p => p.id !== purchasedOrder.id);
      },
    });
  }

  protected handlePurchasedOrderDownload(purchasedOrder: PurchaseOrder): void {
    const fileUrl = purchasedOrder.po_doc?.file;
    if (!fileUrl) {
      this.snackbarService.show('This purchase order has no attached document.', SnackbarAlertStatus.ERROR);
      return;
    }

    const a = document.createElement('a');
    a.href = fileUrl;
    a.target = '_blank';
    a.click();
  }

  /**
   * Opens the order itself, no download required. The list already carries the full
   * record (lines, entries, totals), so nothing is fetched to show it.
   */
  protected handlePurchasedOrderView(purchasedOrder: PurchaseOrder): void {
    this.viewedPurchaseOrder = purchasedOrder;
    this.modalRef = this.modalService.open(this.purchaseOrderDetailsContent, {
      size: 'xl',
      scrollable: true,
    });
  }

  protected closeDetails(): void {
    this.modalRef?.dismiss();
    this.viewedPurchaseOrder = null;
  }

  /** Opens the edit dialog straight from the details view. */
  protected editViewedPurchaseOrder(): void {
    const purchasedOrder = this.viewedPurchaseOrder;
    if (!purchasedOrder) return;

    this.closeDetails();
    this.handlePurchasedOrderEdit(purchasedOrder);
  }

  /** How much of a line has actually been received, or null when nothing has. */
  protected receivedQuantity(purchasedOrder: PurchaseOrder, item: PORawMaterialItem): number | null {
    const entry = purchasedOrder.entries?.find(e => e.purchase === item.id);

    return entry?.quantity_received ?? null;
  }

  protected handlePurchasedOrderEdit(purchasedOrder: PurchaseOrder): void {
    this.mode = Action.EDIT;
    this.purchaseOrderError = null;
    this.selectedPurchasedOrder = purchasedOrder;
    this.initializePurchasedOrderForm(purchasedOrder);

    const modalOptions: NgbModalOptions = { backdrop: 'static', size: 'xl' };
    this.modalRef = this.modalService.open(this.createPurchaseOrderContent, modalOptions);
  }

  protected onCreatePurchaseOrder(): void {
    this.mode = Action.CREATE;
    this.purchaseOrderError = null;
    this.initializePurchasedOrderForm();
    const modalOptions: NgbModalOptions = { backdrop: 'static', size: 'xl' };
    this.modalRef = this.modalService.open(this.createPurchaseOrderContent, modalOptions);
  }

  protected addItem(): void {
    this.items.push(this.createItemGroup());
  }

  protected removeItem(index: number): void {
    this.items.removeAt(index);
  }

  protected getPurchasedMaterialsData(items: any[]) {
    return items.map(item => ({
      quantity_ordered: item.quantity,
      raw_material: item.raw_material,
      rate_excl_gst: toNumberOrNull(item.rate_excl_gst),
      rate_incl_gst: toNumberOrNull(item.rate_incl_gst),
      // Sent even when untouched (it holds the material's prefilled rate), and 0 is a
      // valid rate for exempt goods — toNumberOrNull keeps it as 0 rather than null.
      gst_percentage: toNumberOrNull(item.gst_percentage),
    }));
  }

  protected onRateExclGstInput(index: number): void {
    const item = this.items.at(index);
    const edits = this.getRateEdits(item);
    edits.excl = true;

    if (!edits.incl) this.deriveRateInclFromExcl(item);
  }

  protected onRateInclGstInput(index: number): void {
    const item = this.items.at(index);
    const edits = this.getRateEdits(item);
    edits.incl = true;

    if (!edits.excl) this.deriveRateExclFromIncl(item);
  }

  /** A new GST % re-derives the auto-filled rate, never the one the user typed. */
  protected onGstPercentageChange(index: number): void {
    const item = this.items.at(index);
    const edits = this.getRateEdits(item);

    if (edits.excl && edits.incl) return;
    if (edits.incl) {
      this.deriveRateExclFromIncl(item);
      return;
    }

    // Neither typed (e.g. rates loaded from an existing PO): excl is the base.
    if (!this.deriveRateInclFromExcl(item)) this.deriveRateExclFromIncl(item);
  }

  protected getLineAmount(item: AbstractControl): number | null {
    const rateInclGst = toNumberOrNull(item.get('rate_incl_gst')?.value);
    const quantity = toNumberOrNull(item.get('quantity')?.value);

    if (rateInclGst === null || quantity === null) return null;

    return roundHalfUp(rateInclGst * quantity);
  }

  protected get estimatedOrderTotal(): number | null {
    const amounts = this.items.controls.map(item => this.getLineAmount(item)).filter(a => a !== null);
    if (!amounts.length) return null;

    return roundHalfUp(amounts.reduce((sum, amount) => sum + amount!, 0));
  }

  /** True when a line's GST % deviates from the material's current default. */
  protected isGstOverridden(item: AbstractControl): boolean {
    const lineGst = toNumberOrNull(item.get('gst_percentage')?.value);
    const materialGst = toNumberOrNull(item.get('raw_material_gst_percentage')?.value);

    if (lineGst === null || materialGst === null) return false;

    return lineGst !== materialGst;
  }

  private getRateEdits(item: AbstractControl): { excl: boolean; incl: boolean } {
    let edits = this.rateEditsByItem.get(item);

    if (!edits) {
      edits = { excl: false, incl: false };
      this.rateEditsByItem.set(item, edits);
    }

    return edits;
  }

  private deriveRateInclFromExcl(item: AbstractControl): boolean {
    if (item.get('gst_percentage')?.invalid) return false;

    const rateExclGst = toNumberOrNull(item.get('rate_excl_gst')?.value);
    if (rateExclGst === null) return false;

    const gstPercentage = toNumberOrNull(item.get('gst_percentage')?.value) ?? 0;
    item.get('rate_incl_gst')?.setValue(roundHalfUp(rateExclGst * (1 + gstPercentage / 100)), { emitEvent: false });

    return true;
  }

  private deriveRateExclFromIncl(item: AbstractControl): boolean {
    if (item.get('gst_percentage')?.invalid) return false;

    const rateInclGst = toNumberOrNull(item.get('rate_incl_gst')?.value);
    if (rateInclGst === null) return false;

    const gstPercentage = toNumberOrNull(item.get('gst_percentage')?.value) ?? 0;
    item.get('rate_excl_gst')?.setValue(roundHalfUp(rateInclGst / (1 + gstPercentage / 100)), { emitEvent: false });

    return true;
  }

  protected savePurchaseOrder(): void {
    this.purchaseOrderForm.markAllAsTouched();
    if (this.purchaseOrderForm.invalid) return;

    this.isPurchasedOrderDataSaving = true;
    const formData = this.purchaseOrderForm.getRawValue();

    const reqBody: PurchaseOrderRequest = {
      date_of_order: formData.date,
      purchased_materials: this.getPurchasedMaterialsData(formData.items),
      supplier: formData.supplier_id,
      warehouse: formData.place_of_delivery,
      company: formData.company_id || null,
    };

    const formDataToSend = new FormData();
    formDataToSend.append('data', JSON.stringify(reqBody));
    formDataToSend.append('invoices', '');

    const request$ =
      this.mode === Action.EDIT
        ? this.purchasedOrderService.patch(this.selectedPurchasedOrder!.id, formDataToSend)
        : this.purchasedOrderService.post(formDataToSend);

    this.purchasedOrderSubscription = request$
      .pipe(finalize(() => (this.isPurchasedOrderDataSaving = false)))
      .subscribe({
        next: (result: PurchaseOrder) => {
          // The backend fills in the missing rate, per-line amounts and total_cost —
          // take its values rather than keeping the locally derived ones.
          if (this.mode === Action.EDIT) {
            const index = this.purchasedOrders.findIndex(po => po.id === result.id);
            if (index > -1) this.purchasedOrders[index] = result;
          } else {
            this.purchasedOrders.unshift(result);
          }
          this.getInitialData();
          this.selectedPurchasedOrder = null;
          this.purchaseOrderError = null;
          this.modalRef?.close();
        },
        error: (error: any) => {
          this.purchaseOrderError = error;
        },
      });
  }

  protected prefillRawMaterialEntryFromPO(po: PurchaseOrder) {
    this.isEditMode = po.entries && po.entries.length > 0;
    this.entryPurchaseOrder = po;

    const supplier = this.suppliers.find(supplier => supplier.id === po.supplier);
    const company = this.companies.find(c => c.id === po.company);

    this.rawMaterialEntryForm.patchValue({
      po_number: po.po_number,
      supplier_id: po.supplier,
      supplier_name: supplier?.name ?? po.supplier_name,
      supplier_gst: supplier?.gst_number,
      company_name: company?.name ?? '',
      date_of_order: po.date_of_order,
      created_by: po.created_by_name,
      po: po.id,
      date_of_receipt: po.date_of_receipt,
      invoice_number: po.invoice_number,
      invoice_date: po.invoice_date,
      payment_terms: po.payment_terms,
      payment_status: po.payment_status,
      approval_status: po.is_approved,
    });

    this.poItems.clear();

    po.raw_materials.forEach(poItem => {
      const material = this.rawMaterials.find(m => m.id === poItem.raw_material);

      const entry = po.entries.find(entry => entry.purchase === poItem?.id);

      const poItemGroup = new FormGroup({
        purchase: new FormControl({ value: poItem.id, disabled: true }),
        material_name: new FormControl({ value: poItem.raw_material_name, disabled: true }),
        material_code: new FormControl({ value: material?.code || '', disabled: true }),
        quantity_ordered: new FormControl({ value: poItem.quantity_ordered, disabled: true }),
        quantity_received: new FormControl(entry?.quantity_received, [Validators.required, Validators.min(1)]),
        uom: new FormControl({ value: poItem.unit_name, disabled: true }),
        batch_no: new FormControl(entry?.batch_number),
        qc_status: new FormControl(entry?.quality_check_status),
        qc_remarks: new FormControl(entry?.quality_check_remarks),
        warehouse_id: new FormControl(entry?.warehouse?.id ?? po.warehouse),
        po_warehouse_id: new FormControl({ value: po.warehouse, disabled: true }),
        rate_excl_gst: new FormControl({ value: poItem.rate_excl_gst, disabled: true }),
        rate_incl_gst: new FormControl({ value: poItem.rate_incl_gst, disabled: true }),
        gst_percentage: new FormControl({ value: toNumberOrNull(poItem.gst_percentage), disabled: true }),
        raw_material_gst_percentage: new FormControl({
          value: toNumberOrNull(poItem.raw_material_gst_percentage) ?? toNumberOrNull(material?.gst_percentage),
          disabled: true,
        }),
        amount_incl_gst: new FormControl({ value: toNumberOrNull(poItem.amount_incl_gst), disabled: true }),
      });

      this.poItems.push(poItemGroup);
    });

    this.rawMaterialEntryError = null;
    this.modalRef = this.modalService.open(this.createRawMaterialEntryContent, { size: 'xl', backdrop: 'static' });
  }

  protected saveRawMaterialEntry(): void {
    this.rawMaterialEntryForm.markAllAsTouched();
    if (this.rawMaterialEntryForm.invalid) return;

    this.isUpdatingPurchasedOrderData = true;
    const poId = this.rawMaterialEntryForm.get('po')?.value;
    const formValue = this.rawMaterialEntryForm.value;

    const poUpdateReqBody = this.buildPurchaseOrderUpdateBody(formValue);
    const reqBody = this.buildEntriesRequestBody(poUpdateReqBody.date_of_receipt);

    const formDataToSend = new FormData();
    formDataToSend.append('data', JSON.stringify(poUpdateReqBody));

    const handleError = (error: any) => {
      this.isUpdatingPurchasedOrderData = false;
      this.rawMaterialEntryError = error;
    };

    if (this.isEditMode) {
      this.purchasedOrderSubscription = this.purchasedOrderService
        .update_purchased_order_entry(poId, reqBody)
        .subscribe({
          next: () => this.updatePurchaseOrder(poId, formDataToSend),
          error: handleError,
        });
    } else {
      this.purchasedOrderSubscription = this.purchasedOrderService
        .create_purchased_order_entry(poId, reqBody)
        .subscribe({
          next: () => this.updatePurchaseOrder(poId, formDataToSend),
          error: handleError,
        });
    }
  }

  /**
   * Exports every PO matching the list's current filters. The file is one row per
   * line item, so its row count is not the number of purchase orders shown here.
   */
  protected exportPurchaseOrders(): void {
    this.isExporting = true;

    this.purchasedOrderService
      .export_purchase_orders(this.currentFilters)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: response => {
          // A JSON body here means the server answered with an error, not a workbook.
          if (!isFileBlobResponse(response)) {
            this.snackbarService.show(SnackbarAlert.SOMETHING_WENT_WRONG, SnackbarAlertStatus.ERROR);
            return;
          }

          downloadBlobResponse(response, 'Purchase_Orders_Report.xlsx');
        },
        error: (error: HttpErrorResponse) => {
          blobErrorMessage(error, SnackbarAlert.SOMETHING_WENT_WRONG).subscribe(message =>
            this.snackbarService.show(message, SnackbarAlertStatus.ERROR)
          );
        },
      });
  }

  protected onWareHouseSelected(warehouseId: string): void {
    this.selectedWarehouse = warehouseId;

    this.onFilterChange();
  }

  protected onFilterChange(): void {
    this.currentPage = 1;
    this.getPurchasedOrdersByFilter();
  }

  protected get hasActiveFilters(): boolean {
    return !!(this.selectedWarehouse || this.selectedCompany || this.selectedSupplier);
  }

  protected clearFilters(): void {
    // A plant head is pinned to their own warehouse, so that one is not theirs to clear.
    if (this.currentUser?.role === UserRole.ADMIN) {
      this.selectedWarehouse = null;
    }

    this.selectedCompany = null;
    this.selectedSupplier = null;

    this.onFilterChange();
  }

  /** Relation filters all take a UUID, never a name. */
  private get currentFilters(): { warehouse: string | null; company: string | null; supplier: string | null } {
    return {
      warehouse: this.selectedWarehouse,
      company: this.selectedCompany,
      supplier: this.selectedSupplier,
    };
  }

  private buildPurchaseOrderUpdateBody(formValue: any) {
    return {
      invoice_date: formValue.invoice_date,
      invoice_number: formValue.invoice_number,
      date_of_receipt: formValue.date_of_receipt,
      invoice_quantity: formValue.invoice_quantity,
      is_approved: formValue.approval_status,
      payment_status: formValue.payment_status?.toUpperCase(),
      payment_total: formValue.payment_total,
      payment_terms: formValue.payment_terms,
    };
  }

  private buildEntriesRequestBody(dateOfReceipt: string) {
    return {
      entries: this.poItems.controls.map(itemFormGroup => {
        const v = itemFormGroup.getRawValue();
        const entry: any = {
          purchase: v.purchase,
          quantity_received: v.quantity_received,
        };
        if (dateOfReceipt) entry.date_received = dateOfReceipt;
        if (v.batch_no) entry.batch_number = v.batch_no;
        if (v.qc_status) entry.quality_check_status = v.qc_status;
        if (v.qc_remarks) entry.quality_check_remarks = v.qc_remarks;
        if (v.warehouse_id) entry.warehouse = v.warehouse_id;
        return entry;
      }),
    };
  }

  private updatePurchaseOrder(poId: string, formData: FormData) {
    this.purchasedOrderSubscription = this.purchasedOrderService.patch(poId, formData).subscribe({
      next: () => {
        this.isEditMode = false;
        this.modalRef?.dismiss();
        this.isUpdatingPurchasedOrderData = false;
        this.currentPage = 1;
        this.getPurchasedOrdersByFilter();
      },
    });
  }

  protected onDocumentUpload(event: Event): void {
    const file = (event.target as HTMLInputElement)?.files?.[0];
    if (file) {
      // handle file upload logic
      console.log('Uploaded file:', file);
    }
  }

  private initializeRawMaterialEntryForm(): void {
    this.rawMaterialEntryForm = new FormGroup({
      po_number: new FormControl({ value: '', disabled: true }),
      supplier_id: new FormControl({ value: '', disabled: true }),
      supplier_name: new FormControl({ value: '', disabled: true }),
      supplier_gst: new FormControl({ value: '', disabled: true }),
      company_name: new FormControl({ value: '', disabled: true }),
      date_of_order: new FormControl({ value: '', disabled: true }),
      date_of_receipt: new FormControl(''),
      invoice_number: new FormControl(''),
      invoice_date: new FormControl(''),
      payment_terms: new FormControl(''),
      payment_status: new FormControl(Status.PENDING),
      created_by: new FormControl(''),
      approval_status: new FormControl(Status.PENDING),
      items: new FormArray([]),
      po: new FormControl({ value: '', disabled: true }),
    });
  }

  private initializePurchasedOrderForm(order?: PurchaseOrder): void {
    const supplier = this.suppliers.find(supplier => order?.supplier === supplier.id);

    this.purchaseOrderForm = new FormGroup({
      date: new FormControl(order?.date_of_order || '', Validators.required),
      company_id: new FormControl(order?.company || null),
      supplier_id: new FormControl(order?.supplier || null, Validators.required),
      supplier_name: new FormControl({ value: order?.supplier_name || '', disabled: true }),
      address: new FormControl({ value: supplier?.address ?? '', disabled: true }),
      email: new FormControl({ value: supplier?.email ?? '', disabled: true }),
      gst_number: new FormControl({ value: supplier?.gst_number ?? '', disabled: true }),
      pan_card_number: new FormControl({ value: supplier?.pan_card_number ?? '', disabled: true }),
      udyam_number: new FormControl({ value: supplier?.udyam_number ?? '', disabled: true }),
      place_of_delivery: new FormControl(order?.warehouse || null, Validators.required),
      items: new FormArray(order?.raw_materials?.map(item => this.createItemGroup(item)) || [this.createItemGroup()]),
    });

    this.purchaseOrderForm.get('supplier_id')?.valueChanges.subscribe(id => {
      const supplier = this.suppliers.find(s => s.id === id);
      if (supplier) {
        this.purchaseOrderForm.patchValue({
          supplier_name: supplier.name,
          address: supplier.address,
          gst_number: supplier.gst_number,
          pan_card_number: supplier.pan_card_number,
          udyam_number: supplier.udyam_number,
          email: supplier.email,
        });
      }
    });
  }

  private createItemGroup(item?: PORawMaterialItem): FormGroup {
    return new FormGroup({
      raw_material: new FormControl(item?.raw_material || null, Validators.required),
      quantity: new FormControl(item?.quantity_ordered || 0),
      unit: new FormControl({ value: item?.unit_name ?? '', disabled: true }),
      rate_excl_gst: new FormControl(item?.rate_excl_gst ?? null),
      rate_incl_gst: new FormControl(item?.rate_incl_gst ?? null),
      gst_percentage: new FormControl(toNumberOrNull(item?.gst_percentage), gstPercentageValidator),
      raw_material_gst_percentage: new FormControl({
        // Older PO lines don't carry the material default; fall back to the loaded material.
        value:
          toNumberOrNull(item?.raw_material_gst_percentage) ??
          toNumberOrNull(this.rawMaterials.find(m => m.id === item?.raw_material)?.gst_percentage),
        disabled: true,
      }),
    });
  }

  private getInitialData(): void {
    const q = {
      page: this.currentPage,
      ...this.currentFilters,
    };

    const rawMaterials$ = this.rawMaterialService.list();
    const suppliers$ = this.supplierService.list();
    const measurementUnits$ = this.rawMaterialService.get_units();
    const purchasedOrders$ = this.purchasedOrderService.list(q);
    const companies$ = this.companyFactoryService.listCompanies();
    const supplierOptions$ = this.supplierService.light_list_suppliers();

    this.isPurchasedOrdersDataLoading = true;
    forkJoin([rawMaterials$, suppliers$, measurementUnits$, purchasedOrders$, companies$, supplierOptions$])
      .pipe(finalize(() => (this.isPurchasedOrdersDataLoading = false)))
      .subscribe({
        next: ([
          rawMaterialsListResponse,
          suppliersListResponse,
          measurementUnitsResponse,
          purchasedOrders,
          companiesResponse,
          supplierOptionsResponse,
        ]) => {
          this.rawMaterials = rawMaterialsListResponse;
          this.suppliers = suppliersListResponse.results ?? [];
          this.purchasedOrders = purchasedOrders.results;
          this.measurementUnits = measurementUnitsResponse;
          this.companies = companiesResponse;
          this.supplierOptions = supplierOptionsResponse ?? [];
          this.totalCount = purchasedOrders.count ?? 0;
          this.hasNextPage = !!purchasedOrders.next;
          this.hasPreviousPage = !!purchasedOrders.previous;
        },
      });
  }

  private getPurchasedOrdersByFilter(): void {
    const q = {
      page: this.currentPage,
      ...this.currentFilters,
    };

    this.purchasedOrderSubscription?.unsubscribe();
    this.purchasedOrderSubscription = this.purchasedOrderService.list(q).subscribe({
      next: (purchaseOrdersResponse: PaginatedResponse<PurchaseOrder>) => {
        this.purchasedOrders = purchaseOrdersResponse.results;
        this.totalCount = purchaseOrdersResponse.count;
        this.hasNextPage = !!purchaseOrdersResponse.next;
        this.hasPreviousPage = !!purchaseOrdersResponse.previous;
      },
    });
  }

  onRawMaterialChange(index: number) {
    const item = this.items.at(index);
    const selectedMaterialId = item.get('raw_material')?.value;
    const selectedMaterial = this.rawMaterials.find(m => m.id === selectedMaterialId);

    if (!selectedMaterial) return;

    // The material list already carries gst_percentage, so no extra request is needed.
    const materialGst = toNumberOrNull(selectedMaterial.gst_percentage);

    item.patchValue({
      unit: selectedMaterial.unit?.name ?? '',
      gst_percentage: materialGst,
      raw_material_gst_percentage: materialGst,
    });

    this.onGstPercentageChange(index);
  }

  ngOnDestroy(): void {
    this.modalRef?.dismiss();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.getPurchasedOrdersByFilter();
  }

  /** Blank for lines created before GST support; 0% is a real rate and is shown as such. */
  protected gstDisplay(value: unknown): string {
    const gst = toNumberOrNull(value);

    return gst === null ? '—' : `${gst}%`;
  }

  protected readonly Action = Action;
  protected readonly UserRole = UserRole;
  protected readonly formatAmount = formatAmount;
}
